import { DataProvider, useDataProvider } from 'react-admin';
import {
    QueryClient,
    useQuery,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult,
} from 'react-query';

import {
    GetTreeParams,
    GetTreeResult,
    TreeDataProvider,
    TreeRecord,
} from '../types';
import { getRootAndChildNodes } from '../util';

/**
 * Call the dataProvider.getTree() method and return the resolved value
 * as well as the loading state.
 *
 * The return value updates according to the request state:
 *
 * - start: { isLoading: true, refetch }
 * - success: { data: [data from store], total: [total from response], isLoading: false, refetch }
 * - error: { error: [error from response], isLoading: false, refetch }
 *
 * This hook will return the cached result when called a second time
 * with the same parameters, until the response arrives.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Params} params The getTree parameters { meta }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 *
 * @typedef Params
 * @prop params.meta Optional meta parameters
 *
 * @returns The current request state. Destructure as { data, error, isLoading, isFetching }.
 *
 * The data structure is a flat array of TreeRecords (i.e. Records with a children field containing an array of Identifiers).
 *
 * @example // sample { data }
 * [
 *   { id: 1, title: 'foo1', children: [3, 4] },
 *   { id: 2, title: 'foo2', children: [] },
 *   { id: 3, title: 'foo3', children: [5] },
 *   { id: 4, title: 'foo4', children: [] },
 *   { id: 5, title: 'foo5', children: [] },
 * ]
 *
 * @example // usage in a React component
 *
 * import { useGetTree } from '@react-admin/ra-tree';
 *
 * const Categories = () => {
 *     const { data: tree, isLoading, error } = useGetTree('categories');
 *     if (isLoading) { return <Loading />; }
 *     if (error) { return <p>ERROR</p>; }
 *     return <Tree tree={data} />;
 * };
 */
export const useGetTree = <RecordType extends TreeRecord = TreeRecord>(
    resource: string,
    params: GetTreeParams = {},
    options?: UseQueryOptions<GetTreeResult<RecordType>['data'], Error>
): UseGetTreeValue<RecordType> => {
    const dataProvider = useDataProvider<TreeDataProvider & DataProvider>();
    const queryClient = useQueryClient();
    const result = useQuery<
        GetTreeResult<RecordType>['data'],
        Error,
        GetTreeResult<RecordType>['data']
    >(
        [resource, 'getTree', params],
        () =>
            dataProvider
                .getTree<RecordType>(resource, params)
                .then(({ data }) => data),
        {
            onSuccess: data => {
                updateQueryCacheFromGetTree(
                    queryClient,
                    resource,
                    data,
                    params
                );
            },
            ...options,
        }
    );

    return result as UseGetTreeValue<RecordType>;
};

export type UseGetTreeValue<RecordType extends TreeRecord = TreeRecord> =
    UseQueryResult<RecordType[], Error> & GetTreeResult<RecordType>['data'];

export const updateQueryCacheFromGetTree = <RecordType extends TreeRecord>(
    queryClient: QueryClient,
    resource: string,
    data: RecordType[],
    params: GetTreeParams
) => {
    if (!data) {
        return { rootNodes: [], childNodes: [] };
    }
    const { rootNodes, childNodes } = getRootAndChildNodes(data);

    // optimistically populate the getRootNodes cache
    queryClient.setQueryData(
        [resource, 'getRootNodes', params],
        oldRecords => oldRecords ?? rootNodes
    );

    // optimistically populate the getParentNode cache
    childNodes.forEach(record => {
        queryClient.setQueryData(
            [resource, 'getParentNode', { childId: record.id }],
            oldRecord =>
                oldRecord ??
                data.find(
                    parent =>
                        parent.children && parent.children.includes(record.id)
                )
        );
    });

    // optimistically populate the getChildNodes cache
    data.forEach(record => {
        if (record.children) {
            queryClient.setQueryData(
                [resource, 'getChildNodes', { parentId: record.id }],
                oldRecords =>
                    oldRecords ??
                    data.filter(child => record.children.includes(child.id))
            );
        }
    });
};
